import { z } from "zod";
import { expenseCategorySchema } from "./category.js";
import { groupKindSchema } from "./group.js";
import { avatarTintSchema } from "./identity.js";
import { CURRENCIES } from "./money.js";

/**
 * Le persone con cui dividi delle spese.
 *
 * ── Perché non c'è un totale ───────────────────────────────────────────────
 *
 * Verrebbe naturale scrivere «Gioia ti deve 340 € in tutto» sommando i gruppi.
 * Non si può ancora, e non per pigrizia: in Qota **una persona non esiste
 * fuori da un gruppo** — il partecipante è una riga di `group_members`, ed è ciò
 * che permette a chi non ha un account di partecipare ai conti e di ereditare
 * il proprio saldo il giorno che si iscrive.
 *
 * Sommare fra gruppi richiede di sapere che due righe sono la stessa persona.
 * Per chi ha un account si può (`user_id`); per un segnaposto **è impossibile**,
 * perché non ha identità fuori dal suo gruppo. Un totale che funziona solo per
 * metà delle persone è peggio di nessun totale.
 *
 * Quindi qui i conti restano separati per gruppo. È anche l'unica forma
 * onesta: un debito esiste dentro un gruppo, non in astratto.
 */

/** Chi è la persona, e quanto in là arriva la sua identità. */
export const friendKindSchema = z.enum(["user", "placeholder"]);
export type FriendKind = z.infer<typeof friendKindSchema>;

export const friendGroupSchema = z.object({
  groupId: z.string(),
  groupName: z.string(),
  /**
   * `pair` è il legame diretto fra voi due; `group` un gruppo vero.
   *
   * Serve perché il nome di una coppia è un'etichetta di servizio che non va
   * mai mostrata: l'interfaccia scrive «Solo voi due».
   */
  kind: groupKindSchema,
  currency: z.enum(CURRENCIES),
  /**
   * Quanto è previsto che passi fra te e questa persona, in questo gruppo.
   *
   * Positivo: deve darti dei soldi. Negativo: glieli devi tu. Zero: fra voi
   * due non è previsto nessun passaggio — che in un gruppo di più persone può
   * succedere anche se entrambi avete un saldo aperto, perché la
   * semplificazione dei debiti può indirizzarli verso qualcun altro.
   */
  amountCents: z.number().int(),
});

export type FriendGroup = z.infer<typeof friendGroupSchema>;

export const friendResponseSchema = z.object({
  /**
   * Identificatore stabile della persona **per questa lista**.
   *
   * `user:<id>` per chi ha un account, e vale in tutti i gruppi.
   * `member:<id>` per un segnaposto, e vale solo nel suo. Due segnaposto con lo
   * stesso nome in due gruppi diversi sono due persone diverse: è la verità del
   * modello, non un'approssimazione.
   */
  key: z.string(),
  kind: friendKindSchema,
  displayName: z.string(),
  nickname: z.string().nullable(),
  avatarTint: avatarTintSchema.nullable(),
  /** I gruppi che avete in comune, dal conto più grosso. */
  groups: z.array(friendGroupSchema),
});

export type FriendResponse = z.infer<typeof friendResponseSchema>;

export const friendsResponseSchema = z.object({
  friends: z.array(friendResponseSchema),
});

export type FriendsResponse = z.infer<typeof friendsResponseSchema>;

/**
 * Chiave di una persona in un indirizzo: `user:<id>` oppure `member:<id>`.
 *
 * Validata alla lettera, perché finisce in un URL: senza vincolo di forma
 * sarebbe una stringa arbitraria che arriva fino a una query.
 */
export const friendKeySchema = z.string().regex(/^(user|member):[A-Za-z0-9_-]{1,64}$/);

/** Una spesa che riguarda tutte e due, con il gruppo in cui vive. */
export const sharedExpenseSchema = z.object({
  id: z.string(),
  groupId: z.string(),
  groupName: z.string(),
  description: z.string(),
  category: expenseCategorySchema,
  amountCents: z.number().int(),
  currency: z.enum(CURRENCIES),
  spentAt: z.iso.datetime(),
  payerNames: z.array(z.string()),
});

export type SharedExpense = z.infer<typeof sharedExpenseSchema>;

/**
 * La scheda di una persona.
 *
 * Estende la riga dell'elenco — quindi anche qui **niente totale dei saldi**,
 * per la ragione descritta sopra. `sharedTotalCents` è un'altra cosa: è quanto
 * valgono le spese che vi riguardano, non chi deve cosa a chi.
 */
export const friendDetailResponseSchema = friendResponseSchema.extend({
  sharedExpenseCount: z.number().int().nonnegative(),
  sharedTotalCents: z.number().int().nonnegative(),
  expenses: z.array(sharedExpenseSchema),
  /** `spentAt` da passare come `before` per la pagina successiva. */
  nextBefore: z.iso.datetime().nullable(),
});

export type FriendDetailResponse = z.infer<typeof friendDetailResponseSchema>;

export const SHARED_EXPENSES_PAGE_SIZE = 20;

/** Chi si può aggiungere: solo persone registrate, trovate per nickname. */
export const addFriendRequestSchema = z.object({
  nickname: z.string().min(1).max(30),
});

export type AddFriendRequest = z.infer<typeof addFriendRequestSchema>;

export const addFriendResponseSchema = z.object({
  key: z.string(),
  groupId: z.string(),
  /** `false` se eravate già amici: premere due volte non crea due coppie. */
  created: z.boolean(),
});

export type AddFriendResponse = z.infer<typeof addFriendResponseSchema>;

/**
 * Il risultato di una ricerca per nickname.
 *
 * Espone l'esistenza di un account, ed è una scelta consapevole: senza, non
 * c'è modo di aggiungere qualcuno. Il giorno che servisse, la difesa è
 * un'impostazione di privacy sul profilo, non l'assenza di questa rotta.
 */
export const userLookupResponseSchema = z.object({
  userId: z.string(),
  name: z.string(),
  nickname: z.string(),
  avatarTint: avatarTintSchema,
  /** `true` se siete già amici o condividete un gruppo. */
  alreadyKnown: z.boolean(),
});

export type UserLookupResponse = z.infer<typeof userLookupResponseSchema>;
